import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';
import { ArrowLeft, Plug, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import {
  registerResidentCharger,
  lookupChargerByIdentity,
  fetchMyCharger,
  fetchMyChargers,
  OCPP_WS_BASE,
  type RegisterResidentChargerBody,
  type LocationTypeaheadRow,
  type ChargerLookupResult,
  type MyCharger,
  type MyChargerDetail,
} from '@/lib/api';
import { ConnectionDetailsCard } from '@/components/home/ConnectionDetailsCard';
import {
  alreadyRegisteredFrom, connectionViewFor, isNetworkIncompatibleModel,
} from '@/lib/homeChargerConnection';
import { LocationTypeahead } from '@/components/ui/LocationTypeahead';
import { PhoneCaptureModal } from '@/components/ui/PhoneCaptureModal';
import { EarnPromptCard } from '@/components/home/EarnPromptCard';
import type { UtilityRateEstimate } from '@/lib/hostMargin';
import formStyles from '@/components/Form.module.css';
import { toast } from '@/store/toast';
import { useAuthStore } from '@/store/auth';
import styles from './AddChargerPage.module.css';
import { FormError, useFormError } from '@/components/ui/FormError';

interface Props { onNavigate: (path: string) => void }

type ConnectorType = 'J1772' | 'NACS' | 'CCS1';
const CONNECTOR_OPTIONS: ConnectorType[] = [
  'J1772', 'NACS', 'CCS1',
];

/** Identity charset must match the API's body schema (regex
 *  ^[A-Za-z0-9_-]+$). Catching this client-side avoids a round-trip
 *  on the most common typo (a space). */
const IDENTITY_RE = /^[A-Za-z0-9_-]+$/;

/**
 * Resident self-service charger registration form. Register-first
 * (2026-10-02): the charger does not need to be online. A registration
 * lands on the connection details (server URL, charger ID, password on
 * reveal) the owner types into the charger's app, then Next carries on.
 * We surface the structured error codes as friendly inline copy so the
 * driver knows exactly what to fix; a taken identity is one answer with
 * the support link.
 *
 * A home registration ends on the earn question (EarnPromptCard) rather
 * than the list: the walk at /my-chargers/:id/earn is where a new host
 * is most likely to say yes, and the charger page is one tap away if
 * not. A building registration lands on the charger page directly.
 */
export function AddChargerPage({ onNavigate }: Props) {
  const { t } = useTranslation('myChargersManage');
  // The earn question's copy lives beside the charger page's other
  // monetization strings (myChargers), not this form's namespace.
  const { t: tChargers } = useTranslation('myChargers');
  // The model warning is the My Home form's copy.
  const { t: tHome } = useTranslation('myHome');
  // The not-a-resident refusal names the Menu tab, its Account sheet and
  // the item there that joins a building, in the driver's language.
  const { t: tMenu } = useTranslation('menu');
  const { t: tCommon } = useTranslation('common');
  const { t: tEarn } = useTranslation('earn');
  const qc = useQueryClient();

  // Set on a successful registration that carries connection details: the
  // form gives way to the setup card, and Next continues to the earn
  // question (home) or the charger page (building).
  const [setup, setSetup] = useState<MyCharger | null>(null);
  // While the setup card shows, the shared my-chargers list (the 10 s
  // foreground poll My Home and the list page use) keeps it current: the
  // window closing, provisioning, connected. The 201 until the list has it.
  const liveQ = useQuery({
    queryKey: ['my-chargers'],
    queryFn: fetchMyChargers,
    enabled: setup !== null,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });
  const liveSetup = setup ? liveQ.data?.find((c) => c._id === setup._id) ?? setup : null;
  // Set on the 409 CHARGER_ALREADY_REGISTERED: the support link under the
  // error, with the identity as the ticket's context.
  const [supportUrl, setSupportUrl] = useState<string | null>(null);

  // Set on a successful HOME registration: the form gives way to the
  // "Want to earn from it?" card, and the page only leaves when the
  // owner picks a button. Null while the form is up.
  const [earnPrompt, setEarnPrompt] = useState<{
    id: string;
    utilityRate: UtilityRateEstimate | null;
    /** EVI's connection fee the new charger was created with. */
    connectionFeeCents: number | null;
    /** The home's currency (item 3). */
    currency: string | null;
  } | null>(null);

  const [locationId, setLocationId] = useState<string | null>(null);
  const [, setLocationRow] = useState<LocationTypeaheadRow | null>(null);
  const [identity, setIdentity] = useState('');
  const [connectorType, setConnectorType] = useState<ConnectorType>('J1772');
  const [maxKw, setMaxKw] = useState<string>('11');
  const [vendor, setVendor] = useState('');
  const [model, setModel] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useFormError();
  // Set when the registration POST returns 412 PHONE_REQUIRED. The
  // PhoneCaptureModal opens; on capture we replay the registration
  // body and continue. Cleared on cancel or successful retry.
  const [phonePromptOpen, setPhonePromptOpen] = useState(false);

  // Auto-populate state. Driven by debounced lookups against
  // /chargers/lookup-by-identity — when the identity matches a
  // known/connected charger, we pre-fill vendor/model/connector
  // specs and surface a "found" indicator. The submit gate is
  // unchanged: a charger already registered to someone else still
  // 409s on POST.
  const [lookupResult, setLookupResult] = useState<ChargerLookupResult | null>(null);
  const [looking, setLooking] = useState(false);
  // Track the identity used for the most recent successful prefill
  // so we can avoid clobbering user-typed overrides if they edit a
  // field after the lookup populated it. Each new identity
  // re-prefills regardless.
  const lastFilledForRef = useRef<string | null>(null);
  const lookupTimer = useRef<number | null>(null);

  // Debounced identity lookup. Fires 500ms after the user stops
  // typing (cleared on each keystroke) AND on blur. Skips when:
  //   - identity is empty / invalid alphabet (would be a wasted
  //     round-trip; the form-layer validator catches it on submit)
  //   - identity already matches the last-prefilled value (we
  //     don't want to keep clobbering user-entered overrides on
  //     every render)
  // Re-runs when the identity changes to a different valid value
  // — clears stale prefill and refetches.
  useEffect(() => {
    const trimmed = identity.trim();
    if (!trimmed || !IDENTITY_RE.test(trimmed)) {
      // Blank or bad input — drop any prior prefill marker and
      // any pending timer. The lookupResult banner stays visible
      // until a user types a fresh valid identity that resolves.
      if (lookupTimer.current !== null) window.clearTimeout(lookupTimer.current);
      return;
    }
    if (lastFilledForRef.current === trimmed) return;

    if (lookupTimer.current !== null) window.clearTimeout(lookupTimer.current);
    lookupTimer.current = window.setTimeout(() => {
      void runLookup(trimmed);
    }, 500);
    return () => {
      if (lookupTimer.current !== null) window.clearTimeout(lookupTimer.current);
    };
  }, [identity]);

  const runLookup = async (idStr: string): Promise<void> => {
    setLooking(true);
    try {
      const result = await lookupChargerByIdentity(idStr);
      // Race guard — by the time the request resolves, the user
      // may have typed past the value we looked up. Drop stale
      // results so we don't apply prefill from an old query.
      if (idStr !== identity.trim()) return;

      setLookupResult(result);
      if (result.found) {
        if (result.vendor) setVendor(result.vendor);
        if (result.model) setModel(result.model);
        const firstConnector = result.connectors?.[0];
        if (firstConnector) {
          // Connector type from the DB doc may not be one of our
          // 5 known types (legacy 'Tesla' etc.). Default to Type2
          // when it isn't a match — the resident can override.
          const t = firstConnector.type as ConnectorType;
          if (CONNECTOR_OPTIONS.includes(t)) {
            setConnectorType(t);
          }
          setMaxKw(String(firstConnector.max_power_kw));
        }
        lastFilledForRef.current = idStr;
      } else {
        // Not found — clear any previous prefill marker so a
        // subsequent re-type to the same value still attempts a
        // lookup (the charger may have come online since).
        lastFilledForRef.current = null;
      }
    } catch {
      // Lookups are best-effort UX; failures fall through to the
      // user filling fields manually as before.
      setLookupResult(null);
    } finally {
      setLooking(false);
    }
  };

  const handleLocationChange = (
    id: string | null,
    row: LocationTypeaheadRow | null,
  ): void => {
    setLocationId(id);
    setLocationRow(row);
  };

  const validate = (): string | null => {
    if (!locationId) return t('add.validation.building');
    if (!identity.trim()) return t('add.validation.identityRequired');
    if (!IDENTITY_RE.test(identity.trim())) {
      return t('add.validation.identityFormat');
    }
    const kw = Number(maxKw);
    if (!Number.isFinite(kw) || kw < 1 || kw > 350) {
      return t('add.validation.maxPower');
    }
    return null;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrorMsg('');
    setSupportUrl(null);

    const validation = validate();
    if (validation) {
      setErrorMsg(validation);
      return;
    }

    await runRegistration();
  };

  /** Build the registration body from current form state and POST.
   *  Extracted from handleSubmit so the phone-capture retry path
   *  can call it again without re-running form validation (the
   *  fields haven't changed). */
  const runRegistration = async (): Promise<void> => {
    setSubmitting(true);
    try {
      const body: RegisterResidentChargerBody = {
        identity: identity.trim(),
        location_id: locationId!,
        connector_type: connectorType,
        max_power_kw: Number(maxKw),
      };
      if (photoUrl.trim()) body.photo_url = photoUrl.trim();
      if (vendor.trim()) body.vendor = vendor.trim();
      if (model.trim()) body.model = model.trim();

      const created = await registerResidentCharger(body);
      // The driver is a homeowner now: /auth/me re-derives the persona
      // and the home charger id, which flips the home-screen pins to
      // Start my charger / Earnings / Rewards.
      void useAuthStore.getState().refreshUser();

      // Refresh My Chargers list before leaving so the new entry is
      // already there whichever page the driver lands on next.
      await qc.invalidateQueries({ queryKey: ['my-chargers'] });

      toast(t('add.registeredToast'), { tone: 'accent' });

      // Register-first: show what to type into the charger's app before
      // anything else. An API older than register-first sends no block,
      // and every charger it registered was already online.
      if (created.connection) {
        setSetup(created);
        return;
      }
      await continueAfterRegistration(created._id);
    } catch (err) {
      // 412 PHONE_REQUIRED: open the phone-capture modal so the
      // resident can fix their account and we can retry.
      const code = await readErrorCode(err);
      if (code === 'PHONE_REQUIRED') {
        setPhonePromptOpen(true);
        setSubmitting(false);
        return;
      }
      // 403 NOT_A_BUILDING_RESIDENT: only the building's residents register
      // there. Say so in the driver's language, with the way to join.
      if (code === 'NOT_A_BUILDING_RESIDENT') {
        // Menu tab, then the Account sheet, then its Join a building or
        // fleet row (ProfilePage): three taps, each named as shown.
        setErrorMsg(t('add.notResident', {
          tab: tCommon('nav.menu'),
          sheet: tMenu('category.account.label'),
          menu: tMenu('account.joinBuildingOrFleet'),
        }));
        return;
      }
      // 400 HOME_COUNTRY_REQUIRED (item 3): the home's address names no
      // country, so there is no currency to set its tariff in.
      if (code === 'HOME_COUNTRY_REQUIRED') {
        setErrorMsg(tEarn('price.validation.countryRequired'));
        return;
      }
      // 400 LOCATION_COUNTRY_REQUIRED: the same at a building, which only
      // EVI can fix. The server's message is English-only, so say it here,
      // with the way to support.
      if (code === 'LOCATION_COUNTRY_REQUIRED') {
        setErrorMsg(t('add.buildingCountryRequired'));
        setSupportUrl('/support');
        return;
      }
      // 409: already registered, by anyone. One answer and the support
      // form, never a detail about the existing record.
      const taken = await readAlreadyRegistered(err);
      if (taken) {
        setErrorMsg(t('add.conflict'));
        setSupportUrl(taken.supportUrl);
        return;
      }
      // The per-user registration limit (10 an hour): the server's
      // message is English-only, so say it here.
      if ((err as { response?: Response }).response?.status === 429) {
        setErrorMsg(t('add.rateLimited'));
        return;
      }
      const friendly = await mapApiError(err);
      setErrorMsg(friendly);
    } finally {
      setSubmitting(false);
    }
  };

  /** After the setup card (or straight after registering, on an older API):
   *  the earn question at an unenrolled home, else the charger page. */
  const continueAfterRegistration = async (chargerId: string): Promise<void> => {
    // The 201 is the list projection — no home_monetization, no
    // location type — so one detail read tells a home from a building
    // and seeds the charger page's query for wherever the owner goes
    // next. A building registration (home_monetization null) has no
    // earn walk: straight to the charger page, the better landing
    // than the list. Registration already succeeded by now, so a
    // failed follow-up read must never present as a failed
    // registration — it falls through to the charger page too.
    let detail: MyChargerDetail | null = null;
    try {
      detail = await fetchMyCharger(chargerId);
      qc.setQueryData(['my-charger', chargerId], detail);
    } catch {
      // Nothing to recover: the charger page below re-reads it.
    }
    // Only a home that is not earning yet gets the question — a second
    // charger at an enrolled home already earns through the home's
    // enrolment, and the walk would only tell them so.
    if (detail?.home_monetization && !detail.home_monetization.enrolled) {
      setSetup(null);
      setEarnPrompt({
        id: chargerId,
        utilityRate: detail.home_monetization.utility_rate,
        connectionFeeCents: detail.home_monetization.connection_fee_cents,
        currency: detail.home_monetization.currency ?? null,
      });
    } else {
      onNavigate(`/my-chargers/${chargerId}`);
    }
  };

  const canSubmit = !!locationId && identity.trim().length > 0 && !submitting;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backFab}
          aria-label={t('back')}
          onClick={() => onNavigate('/my-chargers')}
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.titleBlock}>
          {/* Once registered the plug-in instructions are done with; the
              header says what happened and the card asks its question. */}
          <h1 className={styles.title}>{earnPrompt || setup ? t('add.registeredTitle') : t('add.title')}</h1>
          {!earnPrompt && !setup && (
            <p className={styles.subtitle}>
              {t('add.subtitle')}
            </p>
          )}
        </div>
      </header>

      {/* Registered at a home: the form is done, ask the one question.
          Yes → the earn walk (price, hours, payouts); Not now → the
          charger page. Both buttons are in flow, so the page's bottom
          padding — not an anchored bar — keeps them above the pill nav. */}
      {earnPrompt && (
        <EarnPromptCard
          title={tChargers('detail.earnPrompt.title')}
          subtitle={tChargers('detail.earnPrompt.subtitle')}
          utilityRate={earnPrompt.utilityRate}
          connectionFeeCents={earnPrompt.connectionFeeCents}
          currency={earnPrompt.currency}
          primaryLabel={tChargers('detail.earnPrompt.yes')}
          onPrimary={() => onNavigate(`/my-chargers/${earnPrompt.id}/earn`)}
          secondaryLabel={tChargers('detail.earnPrompt.notNow')}
          onSecondary={() => onNavigate(`/my-chargers/${earnPrompt.id}`)}
        />
      )}

      {/* Registered: what to type into the charger's app comes first.
          Next continues to the earn question (home) or the charger page. */}
      {setup && liveSetup && !earnPrompt && (
        <>
          <ConnectionDetailsCard
            chargerId={setup._id}
            connection={connectionViewFor(liveSetup, OCPP_WS_BASE)}
            variant="setup"
            onChanged={() => { void liveQ.refetch(); }}
          />
          <button
            type="button"
            className={formStyles.btnPrimary}
            onClick={() => { void continueAfterRegistration(setup._id); }}
          >
            {t('add.next')}
          </button>
        </>
      )}

      {!earnPrompt && !setup && (
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        {errorMsg && (
          <FormError announce={errorMsg.nonce}>
            {errorMsg.message}
          </FormError>
        )}
        {supportUrl && (
          <button
            type="button"
            className={formStyles.btnSecondary}
            onClick={() => onNavigate(supportUrl)}
          >
            {t('add.contactSupport')}
          </button>
        )}

        {/* ── Building picker ─────────────────────────────────── */}
        <div className={formStyles.field}>
          <label className={formStyles.label}>{t('add.buildingLabel')}</label>
          <LocationTypeahead
            selectedId={locationId}
            onChange={handleLocationChange}
            disabled={submitting}
            ariaLabel={t('add.buildingSearchAria')}
          />
          <p className={formStyles.hint}>
            {t('add.buildingHint')}
          </p>
        </div>

        {/* ── Identity ────────────────────────────────────────── */}
        <div className={formStyles.field}>
          <label htmlFor="charger-identity" className={formStyles.label}>
            {t('add.identityLabel')}
          </label>
          <input
            id="charger-identity"
            className={formStyles.input}
            type="text"
            value={identity}
            onChange={(e) => setIdentity(e.target.value)}
            onBlur={() => {
              // Skip the debounce on blur — fire immediately so the
              // user sees the prefill before they tab into the next
              // field.
              const trimmed = identity.trim();
              if (trimmed && IDENTITY_RE.test(trimmed)
                  && lastFilledForRef.current !== trimmed) {
                if (lookupTimer.current !== null) window.clearTimeout(lookupTimer.current);
                void runLookup(trimmed);
              }
            }}
            placeholder={t('add.identityPlaceholder')}
            disabled={submitting}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            maxLength={50}
            required
          />
          {/* Auto-populate indicator. Three states:
              - looking: subtle inline spinner
              - found: green chip with copy explaining the prefill
              - not-found / null: nothing (don't pollute the form
                for normal greenfield registrations) */}
          {looking && (
            <span className={styles.lookupSpinner}>
              <Loader2 size={11} className={styles.spin} /> {t('add.lookingUp')}
            </span>
          )}
          {!looking && lookupResult?.found && (
            <span className={styles.lookupFound}>
              <CheckCircle2 size={12} />
              <span>
                {t('add.lookupFound')}
              </span>
            </span>
          )}
          <p className={formStyles.hint}>
            {t('add.identityHint')}
          </p>
        </div>

        {/* ── Connector type ──────────────────────────────────── */}
        <div className={formStyles.field}>
          <label className={formStyles.label}>{t('add.connectorLabel')}</label>
          <div className={styles.chipGroup} role="radiogroup" aria-label={t('add.connectorAria')}>
            {CONNECTOR_OPTIONS.map((opt) => {
              const active = connectorType === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={active ? styles.chipActive : styles.chip}
                  onClick={() => setConnectorType(opt)}
                  disabled={submitting}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Max power ───────────────────────────────────────── */}
        <div className={formStyles.field}>
          <label htmlFor="charger-kw" className={formStyles.label}>
            {t('add.maxPowerLabel')}
          </label>
          <input
            id="charger-kw"
            className={formStyles.input}
            type="number"
            inputMode="decimal"
            min={1}
            max={350}
            step={0.1}
            value={maxKw}
            onChange={(e) => setMaxKw(e.target.value)}
            disabled={submitting}
            required
          />
          <p className={formStyles.hint}>
            {t('add.maxPowerHint')}
          </p>
        </div>

        {/* ── Optional metadata ───────────────────────────────── */}
        <div className={formStyles.field}>
          <label htmlFor="charger-vendor" className={formStyles.label}>
            {t('add.vendorLabel')}
          </label>
          <input
            id="charger-vendor"
            className={formStyles.input}
            type="text"
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            placeholder={t('add.vendorPlaceholder')}
            disabled={submitting}
            maxLength={200}
          />
        </div>
        <div className={formStyles.field}>
          <label htmlFor="charger-model" className={formStyles.label}>
            {t('add.modelLabel')}
          </label>
          <input
            id="charger-model"
            className={formStyles.input}
            type="text"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder={t('add.modelPlaceholder')}
            disabled={submitting}
            maxLength={200}
          />
          {isNetworkIncompatibleModel(model) && (
            <p className={formStyles.hint} role="alert">
              <AlertTriangle size={12} aria-hidden /> {tHome('registerForm.modelIncompatible', { model: model.trim() })}
            </p>
          )}
        </div>
        <div className={formStyles.field}>
          <label htmlFor="charger-photo" className={formStyles.label}>
            {t('add.photoLabel')}
          </label>
          <input
            id="charger-photo"
            className={formStyles.input}
            type="url"
            value={photoUrl}
            onChange={(e) => setPhotoUrl(e.target.value)}
            placeholder="https://…"
            disabled={submitting}
            maxLength={2048}
          />
          <p className={formStyles.hint}>
            {t('add.photoHint')}
          </p>
        </div>

        <button
          type="submit"
          className={formStyles.btnPrimary}
          disabled={!canSubmit}
        >
          {submitting ? (
            <>
              <Loader2 size={14} className={styles.spin} /> {t('add.registering')}
            </>
          ) : (
            <>
              <Plug size={14} /> {t('add.submit')}
            </>
          )}
        </button>
      </form>
      )}

      {phonePromptOpen && (
        <PhoneCaptureModal
          title={t('add.phone.title')}
          reason={
            <>
              {t('add.phone.reason')}
            </>
          }
          onCapture={async () => {
            setPhonePromptOpen(false);
            await runRegistration();
          }}
          onSkip={() => {
            setPhonePromptOpen(false);
            setErrorMsg(t('add.phone.skipError'));
          }}
        />
      )}
    </div>
  );
}

/** Pull a structured error code out of a ky HTTPError envelope.
 *  Returns null when the error isn't shaped like our standard
 *  { error: { code, message } } payload (network drop, parse fail). */
async function readErrorCode(err: unknown): Promise<string | null> {
  const response = (err as { response?: Response }).response;
  if (!response) return null;
  try {
    const body = await response.clone().json() as { error?: { code?: string } };
    return body.error?.code ?? null;
  } catch {
    return null;
  }
}

/** The 409 CHARGER_ALREADY_REGISTERED body's support link, or null for
 *  any other error. */
async function readAlreadyRegistered(err: unknown): Promise<{ supportUrl: string } | null> {
  const response = (err as { response?: Response }).response;
  if (!response || response.status !== 409) return null;
  try {
    return alreadyRegisteredFrom(await response.clone().json());
  } catch {
    return null;
  }
}

/** Drains the API error envelope and maps known structured codes
 *  to driver-friendly copy. Falls back to the API's own `message`
 *  field where available so we don't lose backend nuance for
 *  validation errors we don't have explicit cases for. */
async function mapApiError(err: unknown): Promise<string> {
  // ky's HTTPError exposes the original Response on .response.
  // Defensive: handle non-ky errors (network drop) too.
  const response = (err as { response?: Response }).response;
  if (!response) {
    return err instanceof Error ? err.message : i18n.t('myChargersManage:add.registerFailed');
  }
  try {
    const body = await response.clone().json() as {
      error?: { code?: string; message?: string };
    };
    const code = body.error?.code;
    const message = body.error?.message;
    switch (code) {
      // Phase 2 of the tenant→location toggle migration — the
      // server-side gate now emits LOCATION_DISABLED_RESIDENT_-
      // REGISTRATION (per-Location scope, line 1843 of
      // apps/api/src/routes/chargers/index.ts). Keep the legacy
      // BUILDING_DISABLED_RESIDENT_REGISTRATION case for one
      // release cycle so any 403s from a stale-deployed pre-Phase-2
      // server don't surface as "Failed to register charger" via
      // the default fallback. Both fall through to the same fallback
      // message; the new code's message gets the same copy. Drop
      // the legacy case in Phase 4 alongside the Tenant field
      // removal.
      case 'LOCATION_DISABLED_RESIDENT_REGISTRATION':
      case 'BUILDING_DISABLED_RESIDENT_REGISTRATION':
        return message
          ?? i18n.t('myChargersManage:add.buildingDisabled');
      case 'CHARGER_ALREADY_REGISTERED':
        return message ?? i18n.t('myChargersManage:add.conflict');
      default:
        return message ?? i18n.t('myChargersManage:add.registerFailed');
    }
  } catch {
    return i18n.t('myChargersManage:add.registerFailed');
  }
}
